import { downloadFileZip, getFileInfo, getFileList } from "@/api/cmmn/FileApi";
import type { FileInfoTypes } from "@/types/FileTypes";
import type { AxiosResponse } from "axios";

/**
 * 파일 유틸리티 함수 모음
 * 에러 처리 및 로딩 관리는 호출하는 쪽에서 처리합니다.
 */
const FileUtil = {
  /**
   * ZIP 파일 다운로드
   * @param _fileName - 다운로드될 파일 이름
   * @param _fileGroupList - 다운로드할 파일 그룹 아이디 리스트 (예: ["170", "150", "199"])
   * @returns Promise<void>
   * @throws 에러 발생 시 throw
   */
  fnGetDownZipFile: async (_fileName: string, _fileGroupList: string[]): Promise<void> => {
    if (_fileGroupList.length === 0) {
      throw new Error("첨부자료가 없습니다.");
    }

    const response = (await downloadFileZip(_fileGroupList)) as unknown as AxiosResponse<Blob>;

    const blob = new Blob([response.data], { type: "application/zip" });
    const downloadUrl = URL.createObjectURL(blob);

    try {
      await FileUtil.fnDownloadFromUrl(downloadUrl, _fileName);
    } finally {
      // Blob URL 정리
      URL.revokeObjectURL(downloadUrl);
    }
  },

  /**
   * 단일 파일 다운로드
   * @param _groupId - 다운로드할 파일 그룹 ID
   * @returns Promise<void>
   * @throws 에러 발생 시 throw
   */
  fnGetDownSingleFile: async (_groupId: string): Promise<void> => {
    const fileList = await getFileList({ groupId: _groupId });

    if (!fileList || fileList.length === 0) {
      throw new Error("파일 정보를 찾을 수 없습니다.");
    }

    const fileInfo = fileList[0] as FileInfoTypes;
    const fileUrl = `${fileInfo.link}${fileInfo.saveName}`;
    const fileNm = `${fileInfo.originalName}.${fileInfo.ext}`;

    // FileApi의 getFileInfo를 사용하여 파일 다운로드
    const blob = await getFileInfo(fileUrl);
    const downloadUrl = URL.createObjectURL(blob);

    try {
      await FileUtil.fnDownloadFromUrl(downloadUrl, fileNm);
    } finally {
      // Blob URL 정리
      URL.revokeObjectURL(downloadUrl);
    }
  },

  /**
   * 파일 다운로드 헬퍼 함수
   * @param path - 파일 경로 또는 Blob URL (내부, 외부 상관없음)
   * @param fileNm - 다운로드될 파일 이름 (확장자 포함)
   * @returns Promise<void>
   * @throws 에러 발생 시 throw
   */
  fnDownloadFromUrl: async (path: string, fileNm: string): Promise<void> => {
    let url: string;

    // Blob URL인 경우 (이미 생성된 URL, 바로 사용 - fetch 불필요, CSP 위반 방지)
    if (path.startsWith("blob:")) {
      url = path;
    } else {
      // 일반 URL인 경우 FileApi의 getFileInfo 사용하여 Blob 생성
      const blob = await getFileInfo(path);
      url = URL.createObjectURL(blob);
    }

    const link = document.createElement("a");

    link.download = fileNm;
    link.href = url;

    document.body.appendChild(link);
    link.click();

    // 다운로드가 시작되도록 약간의 지연 후 링크 제거
    setTimeout(() => {
      document.body.removeChild(link);
    }, 100);
  },
};

export default FileUtil;
